// src/products/dataroom/hooks/useActivityFeed.js
// Custom hook for managing Dataroom activity feed

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../config';

export const useActivityFeed = (projectId) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Load activities
  const loadActivities = useCallback(async (limit = 50, offset = 0) => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);

      // First, get the activities
      const { data: activities, error: activitiesError } = await supabase
        .from('dataroom_logs')
        .select('*')
        .eq('project_id', projectId)
        .order('timestamp', { ascending: false })
        .range(offset, offset + limit - 1);

      if (activitiesError) throw activitiesError;

      if (!activities || activities.length === 0) {
        const newActivities = [];
        if (offset === 0) {
          setActivities(newActivities);
        } else {
          setActivities(prev => [...prev, ...newActivities]);
        }
        setHasMore(false);
        return;
      }

      // Get unique user IDs from activities
      const userIds = [...new Set(activities.map(activity => activity.user_id))];

      // Fetch user profiles for these users
      const { data: userProfiles, error: usersError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email')
        .in('id', userIds);

      if (usersError) throw usersError;

      // Create a map of user profiles
      const userMap = {};
      if (userProfiles) {
        userProfiles.forEach(user => {
          userMap[user.id] = user;
        });
      }

      // Combine activities with user data
      const newActivities = activities.map(activity => ({
        ...activity,
        user_profiles: userMap[activity.user_id] || null
      }));
      
      if (offset === 0) {
        setActivities(newActivities);
      } else {
        setActivities(prev => [...prev, ...newActivities]);
      }

      setHasMore(newActivities.length === limit);
    } catch (err) {
      console.error('Error loading activities:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Load more activities (pagination)
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadActivities(50, activities.length);
    }
  }, [loading, hasMore, activities.length, loadActivities]);

  // Refresh activities
  const refresh = useCallback(() => {
    loadActivities(50, 0);
  }, [loadActivities]);

  // Add new activity (for real-time updates)
  const addActivity = useCallback((activity) => {
    setActivities(prev => [activity, ...prev]);
  }, []);

  // Get activity icon
  const getActivityIcon = useCallback((action) => {
    const iconMap = {
      'project_created': '📁',
      'project_updated': '✏️',
      'file_uploaded': '📤',
      'file_deleted': '🗑️',
      'folder_created': '📂',
      'folder_deleted': '🗑️',
      'user_added': '👤',
      'user_removed': '👤',
      'permissions_changed': '🔐',
      'item_moved': '📦',
      'item_renamed': '🏷️',
      'bookmark_created': '🔖'
    };
    return iconMap[action] || '📝';
  }, []);

  // Get activity color
  const getActivityColor = useCallback((action) => {
    const colorMap = {
      'project_created': 'text-green-600',
      'project_updated': 'text-blue-600',
      'file_uploaded': 'text-green-600',
      'file_deleted': 'text-red-600',
      'folder_created': 'text-green-600',
      'folder_deleted': 'text-red-600',
      'user_added': 'text-green-600',
      'user_removed': 'text-red-600',
      'permissions_changed': 'text-yellow-600',
      'item_moved': 'text-blue-600',
      'item_renamed': 'text-purple-600',
      'bookmark_created': 'text-pink-600'
    };
    return colorMap[action] || 'text-gray-600';
  }, []);

  // Format activity time
  const formatActivityTime = useCallback((timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - activityTime) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      const { formatDate } = require('../utils/dateUtils');
      return formatDate(activityTime);
    }
  }, []);

  // Load data on mount and when projectId changes
  useEffect(() => {
    if (projectId) {
      loadActivities();
    }
  }, [projectId, loadActivities]);

  return {
    // State
    activities,
    loading,
    error,
    hasMore,

    // Actions
    loadActivities,
    loadMore,
    refresh,
    addActivity,

    // Utilities
    getActivityIcon,
    getActivityColor,
    formatActivityTime,

    // Computed values
    hasActivities: activities.length > 0,
    recentActivities: activities.slice(0, 10),
    todayActivities: activities.filter(a => {
      const today = new Date();
      const activityDate = new Date(a.created_at);
      return activityDate.toDateString() === today.toDateString();
    })
  };
};

export default useActivityFeed;
